import './App.scss';
import { AppRoutes } from './routes/AppRoutes';
import { useEffect, useLayoutEffect } from 'react';
import { useJimo } from './hooks/useJimo';
import { Toaster } from 'react-hot-toast';
import infos from '../package.json';
import {
  setupJimo,
  setupLuxon,
  redirectToSubdomain,
  useFetchUser,
  useModalViolationsContext,
  OffCanvasHost,
  JxtModalLocateUser,
  useModalLocateUserContext,
  useInterceptors,
  useModalManageBookingsContext,
  JxtModalManageBookings,
  queryClient,
  UserQueryKeys,
  useJxtRouter,
  isGuestRoute,
  configureCognitoCookieStorage,
} from '@jooxter/core';
import JxtModalBookingsViolations from '@jooxter/core/src/components/JxtModalBookingsViolations';
import { useTranslation } from 'react-i18next';
import { getWindowName } from '@jooxter/utils';
import { Amplify } from 'aws-amplify';
import { sessionStorage as awsSessionStorage } from '@aws-amplify/core';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { guestAmplifyConfig } from './amplify/guest.config';
import { defaultAmplifyConfig } from './amplify/default.config';
import { useGTM } from './hooks/useGtm';
import { useSentryUser } from './sentry';
import { STALE_CHUNK_RELOAD_KEY } from './routes/staleChunk';

const App = () => {
  useJimo();
  useInterceptors(infos.version, getWindowName());
  const modalViolationsContext = useModalViolationsContext();
  const modalLocateUserContext = useModalLocateUserContext();
  const modalManageBookingsContext = useModalManageBookingsContext();
  const { user } = useFetchUser();
  const { i18n } = useTranslation();
  const {
    location: { pathname },
    matchPath,
  } = useJxtRouter();
  useGTM(user, pathname);
  useSentryUser(user);

  useEffect(() => {
    if (user) {
      redirectToSubdomain(user, pathname, i18n.changeLanguage);
      if (user.lang) {
        document.documentElement.lang = user.lang;
      }
      setupLuxon(user);
      setupJimo(user);
    }
    // we don't want the effect to be run when routerLocation.pathName changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.changeLanguage, user]);

  useEffect(() => {
    const handler = () => {
      // Except the stale chunk guard: it must outlive the reload it triggers (routes/staleChunk.ts).
      const staleChunkReload = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
      window.sessionStorage.clear();
      if (staleChunkReload !== null) {
        window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, staleChunkReload);
      }
    };

    window.addEventListener('beforeunload', handler);

    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useLayoutEffect(() => {
    if (isGuestRoute(pathname, matchPath)) {
      Amplify.configure(guestAmplifyConfig);
      cognitoUserPoolsTokenProvider.setKeyValueStorage(awsSessionStorage);
      return;
    }

    Amplify.configure(defaultAmplifyConfig);
    configureCognitoCookieStorage();
  }, [matchPath, pathname]);

  useEffect(() => {
    console.info(`You are serving the react app, version ${infos.version}`);
    // update login details after successful sso login
    queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetUserLoginDetails] });
  }, []);

  return (
    <>
      <AppRoutes />
      <Toaster />

      <OffCanvasHost />
      <JxtModalBookingsViolations
        show={modalViolationsContext?.show ?? false}
        onHide={() => modalViolationsContext?.setShow(false)}
        recurrence={modalViolationsContext?.recurrence ?? null}
      />
      <JxtModalLocateUser
        show={modalLocateUserContext?.show ?? false}
        onHide={() => modalLocateUserContext?.setShow(false)}
        range={modalLocateUserContext?.range}
        date={modalLocateUserContext?.date}
        locateUserModalInfos={modalLocateUserContext?.locateUserModalInfos}
      />
      {!!modalManageBookingsContext && (
        <JxtModalManageBookings {...modalManageBookingsContext.getters} {...modalManageBookingsContext.setters} />
      )}
    </>
  );
};

export default App;
